import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Loader2, DoorOpen } from "lucide-react";
import { toast } from "sonner";
import { useEmps, EmpCell } from "./ListPages";

export default function OffboardingPage() {
  const { t } = useI18n();
  const emps = useEmps();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    employee_id: "", reason: "resignation", last_day: "",
    severance_amount: 0, notice_given: true
  });
  const [loading, setLoading] = useState(false);

  const refresh = () => api.get("/offboarding").then(r => setRows(r.data));
  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    if (!form.employee_id || !form.last_day) return toast.error("Fill employee & last day");
    setLoading(true);
    try {
      await api.post("/offboarding", { ...form, severance_amount: parseFloat(form.severance_amount) || 0 });
      toast.success(t("saved"));
      setForm({ employee_id: "", reason: "resignation", last_day: "", severance_amount: 0, notice_given: true });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    setLoading(false);
  };

  const toggle = async (r, k) => {
    await api.put(`/offboarding/${r.id}`, { [k]: !r[k] });
    refresh();
  };

  return (
    <div className="space-y-5" data-testid="offboarding-page">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><DoorOpen className="w-4 h-4" /> {t("newOffboarding")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">{t("employees")}</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm(f => ({ ...f, employee_id: v }))}>
              <SelectTrigger data-testid="off-emp"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{Object.values(emps).map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("reason")}</Label>
            <Select value={form.reason} onValueChange={(v) => setForm(f => ({ ...f, reason: v }))}>
              <SelectTrigger data-testid="off-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="resignation">Resignation</SelectItem>
                <SelectItem value="termination">Termination</SelectItem>
                <SelectItem value="retirement">Retirement</SelectItem>
                <SelectItem value="mutual">Mutual Agreement</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("lastDay")}</Label>
            <Input data-testid="off-lastday" type="date" value={form.last_day} onChange={e => setForm(f => ({ ...f, last_day: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("severance")}</Label>
            <Input data-testid="off-severance" type="number" value={form.severance_amount} onChange={e => setForm(f => ({ ...f, severance_amount: e.target.value }))} />
          </div>
          <Button data-testid="off-submit" onClick={submit} disabled={loading} className="md:col-span-4 md:w-auto md:ml-auto">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("add")}
          </Button>
        </div>
      </Card>

      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3">{t("checklist")}</h3>
        {rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t("noData")}</div> : (
          <div className="space-y-3">
            {rows.map((r, i) => (
              <div key={r.id} className="p-4 rounded-lg border border-border/60">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="font-medium text-sm"><EmpCell emp={emps[r.employee_id]} /></div>
                    <div className="text-xs text-muted-foreground font-mono-brand">{r.reason} · {t("lastDay")}: {r.last_day} · {t("severance")}: {r.severance_amount || 0}</div>
                  </div>
                  <Badge>{r.reason}</Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { k: "assets_returned", label: t("assetsReturned") },
                    { k: "sgk_notified", label: t("sgkNotified") },
                    { k: "exit_interview_done", label: t("exitInterview") },
                    { k: "final_pay_settled", label: t("finalPay") },
                  ].map(c => (
                    <label key={c.k} data-testid={`check-${c.k}-${i}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={!!r[c.k]} onCheckedChange={() => toggle(r, c.k)} />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
